import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { v, num, ClauseBlock, KeepTogether, SigningBlock, TemplateDocument, TemplateFooter, TemplateLetterhead, } from '../../../atoms/index.js';
import { expandPartyProjection, PartyBlock } from '../../../parties/index.js';
import { parseDateValue, postSigningRule, } from '../../../notification-rules.js';
const TERM_TILLSVIDARE = 'Tills vidare';
const TERM_FIXED = 'Till ett visst datum';
/**
 * Notification rules (R5). Mirrors the two-part variant: the agreement
 * end date is the single lifecycle reminder, with default 3mo/1mo/1wk
 * leads to give the negotiating parties runway.
 */
const forhandlingsordning3PartNotificationRules = [
    postSigningRule({
        ruleKey: 'agreement_end',
        kind: 'agreement_end',
        label: 'Förhandlingsordning löper ut',
        resolveTriggerAt: (values) => values.agreement_term === TERM_TILLSVIDARE
            ? null
            : parseDateValue(values.agreement_end_date),
    }),
];
export const metadata = {
    templateKey: 'forhandlingsordning_3part',
    version: 'v1',
    category: 'Bostadshyressättningsavtal-3part',
    title: 'Förhandlingsordning på bostadshyresmarknaden – trepartsavtal · Version 2026',
    shortTitle: 'Förhandlingsordning — trepartare',
    formNumber: 'Formulär R-03B',
    preamble: 'Undertecknande parter har denna dag träffat följande överenskommelse.',
    status: 'active',
    signingPlacementAnchors: 'per-party-label',
    recommendPersonalNumberInSigningBlock: true,
    partySlots: [
        {
            id: 'lessor',
            label: 'Hyresvärdspart',
            role: 'lessor',
            ordinal: 0,
            required: true,
        },
        {
            id: 'property_assoc',
            label: 'Fastighetsägarförening',
            role: 'other',
            ordinal: 0,
            required: true,
            help: 'Den fastighetsägarförening som företräder hyresvärdsparten i trepartsförhandlingen.',
        },
        {
            id: 'tenant_org',
            label: 'Hyresgästorganisation',
            role: 'lessee',
            ordinal: 0,
            required: true,
            help: 'Organisationen som företräder hyresgästerna i förhandlingsordningen.',
        },
    ],
    // Party projection (ADR-0065). Three org-ish parties, full contact set.
    partyProjection: [
        {
            slot: 'lessor',
            section: 'Hyresvärdspart',
            fields: [
                {
                    attr: 'legal_name',
                    key: 'landlord_name',
                    requiredAtFinal: true,
                    help: 'Fullständigt namn eller företagsnamn',
                },
                { attr: 'id_number', key: 'landlord_org_number', requiredAtFinal: true },
                { attr: 'address', key: 'landlord_address', requiredAtFinal: true },
                { attr: 'email', key: 'landlord_email', requiredAtFinal: true },
                { attr: 'phone', key: 'landlord_phone' },
            ],
        },
        {
            slot: 'property_assoc',
            section: 'Fastighetsägarförening',
            idHint: 'org',
            fields: [
                {
                    attr: 'legal_name',
                    key: 'property_assoc_name',
                    requiredAtFinal: true,
                    help: 'Föreningens namn',
                },
                { attr: 'id_number', key: 'property_assoc_org_number', requiredAtFinal: true },
                { attr: 'address', key: 'property_assoc_address', requiredAtFinal: true },
                { attr: 'email', key: 'property_assoc_email', requiredAtFinal: true },
                { attr: 'phone', key: 'property_assoc_phone' },
            ],
        },
        {
            slot: 'tenant_org',
            section: 'Hyresgästorganisation',
            idHint: 'org',
            fields: [
                {
                    attr: 'legal_name',
                    key: 'tenant_org_name',
                    requiredAtFinal: true,
                    help: 'Hyresgästorganisationens namn',
                },
                { attr: 'id_number', key: 'tenant_org_number', requiredAtFinal: true },
                { attr: 'address', key: 'tenant_org_address', requiredAtFinal: true },
                { attr: 'email', key: 'tenant_org_email', requiredAtFinal: true },
                { attr: 'phone', key: 'tenant_org_phone' },
            ],
        },
    ],
    propertySlots: [
        {
            id: 'fastigheter',
            label: 'Fastigheter',
            multi: true,
            required: true,
            help: 'Lägg till alla fastigheter som förhandlingsordningen omfattar.',
        },
    ],
    notificationRules: forhandlingsordning3PartNotificationRules,
};
export const variableSchema = [
    // Parter (Hyresvärdspart / Fastighetsägarförening / Hyresgästorganisation)
    // — generated from metadata.partyProjection (ADR-0065).
    ...expandPartyProjection(metadata),
    // Omfattning och undantag — multi-property aggregated
    {
        key: 'property_designations',
        label: 'Fastighetsbeteckningar',
        type: 'textarea',
        section: 'Omfattning och undantag',
        requiredAtFinal: true,
        autofill: { kind: 'property_list', field: 'designations' },
        scopes: { imported: true },
    },
    {
        key: 'property_addresses',
        label: 'Samtliga adresser på fastigheterna',
        type: 'textarea',
        section: 'Omfattning och undantag',
        requiredAtFinal: true,
        autofill: { kind: 'property_list', field: 'addresses' },
        help: 'En adress per rad. Hämtas automatiskt från länkade fastigheter.',
        scopes: { imported: true },
    },
    {
        key: 'apartment_count',
        label: 'Antal lägenheter vid undertecknandet',
        type: 'number',
        min: 0,
        integer: true,
        section: 'Omfattning och undantag',
        requiredAtFinal: true,
        // Per-agreement snapshot count — opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    // Avtalstid
    {
        key: 'agreement_term',
        label: 'Avtalstid',
        type: 'select',
        section: 'Avtalstid',
        default: TERM_FIXED,
        options: [
            {
                value: TERM_FIXED,
                label: 'Till ett visst datum',
                code: 'tidsbestamd',
                summary: 'Runs until a fixed end date, then auto-renews one year at a time unless terminated three months before expiry.',
            },
            {
                value: TERM_TILLSVIDARE,
                label: 'Tills vidare',
                code: 'tillsvidare',
                summary: 'Runs indefinitely; terminable in writing to the turn of the year falling at least three months after notice.',
            },
        ],
        scopes: { copy: 'manual' },
    },
    {
        key: 'agreement_start_date',
        label: 'Avtalet gäller från och med',
        type: 'date',
        section: 'Avtalstid',
        requiredAtFinal: true,
        scopes: { imported: { from: 'dates.start', requiredForReview: true }, copy: 'manual' },
    },
    {
        key: 'agreement_end_date',
        label: 'Avtalet gäller till och med',
        type: 'date',
        section: 'Avtalstid',
        requiredAtFinal: true,
        // Existing drafts have no agreement_term → keep showing the end date.
        visibleWhen: { not: { agreement_term: TERM_TILLSVIDARE } },
        scopes: { imported: { from: 'dates.end' }, copy: 'manual' },
    },
];
export const sampleValues = {
    landlord_name: 'Fastighetsbolaget Söder AB',
    landlord_org_number: '556789-1234',
    landlord_address: 'Drottninggatan 100\n111 60 Stockholm',
    landlord_email: 'forhandling@sodernabolag.se',
    landlord_phone: '08-123 45 67',
    property_assoc_name: 'Fastighetsägarna Stockholm',
    property_assoc_org_number: '802001-2345',
    property_assoc_address: 'Alströmergatan 14\n112 47 Stockholm',
    property_assoc_email: 'forhandling@fastighetsagarna.se',
    property_assoc_phone: '08-617 75 00',
    tenant_org_name: 'Hyresgästföreningen',
    tenant_org_number: '802001-5106',
    tenant_org_address: 'Norrlandsgatan 7\n111 43 Stockholm',
    tenant_org_email: 'forhandling@hyresgastforeningen.se',
    tenant_org_phone: '0771-443 443',
    property_designations: 'Söderhuset 14\nSöderhuset 15',
    property_addresses: 'Götgatan 35\n116 21 Stockholm\nGötgatan 37\n116 21 Stockholm',
    apartment_count: 48,
    agreement_term: TERM_FIXED,
    agreement_start_date: '2026-07-01',
    agreement_end_date: '2029-06-30',
};
function Heading(props) {
    return (_jsx("h2", { className: "mt-4 mb-1.5 border-b border-stone-300 pb-1 text-[12px] font-bold", style: { breakInside: 'avoid' }, children: props.children }));
}
/**
 * Verbatim clause body for punkt 5 (Förhandlingsordningens innebörd) and
 * punkt 8 (Förhandlingsframställning), kept as the single source of truth
 * so the FO-4/FO-5 presentation transforms never alter the legal text —
 * the rendered list/inline-numbering is derived from these exact strings.
 *
 * Static clause bodies are verbatim from Haymanot-approved Word templates.
 * DO NOT paraphrase, reorder, or change clause numbers.
 */
export const CLAUSE_4_INTRO = 'Förhandlingsordningen innebär:';
export const CLAUSE_4A_LEAD = 'a) Skyldighet för hyresvärdsparten att en god tid i förväg på eget initiativ påkalla förhandling med hyresgästparten i följande frågor:';
export const CLAUSE_4A_ITEMS = [
    'höjning av hyran för en lägenhet, om inte höjningen avser ett tillval och högst motsvarar ett pris som förhandlingsordningens parter har rekommenderat,',
    'bestämmande av hyran för en ny hyresgäst, om den begärda hyran överstiger den tidigare hyresgästens hyra och höjningen inte avser ett tillval och högst motsvarar ett pris som förhandlingsordningens parter har rekommenderat,',
    'ändring av grunderna för beräkning av sådan särskild ersättning som avses i 12 kap. 19 § jordabalken, eller',
    'ändring av villkor som avser hyresgästernas rätt att använda utrymmen som är avsedda att användas gemensamt av hyresgästerna.',
];
export const CLAUSE_4B = 'b) Skyldighet för part att, under förutsättning att parterna inte kommit överens om annat, påkalla förhandling med motparten i fråga om hyresvillkoren, lägenheternas och husets skick, gemensamma anordningar i huset, övriga boendeförhållanden, i den mån de rör hyresgästerna gemensamt.';
export const CLAUSE_4C = 'c) Rätt för hyresgästparten att, under förutsättning att hyresgästen genom skriftlig fullmakt har gett hyresgästparten rätt att företräda honom eller henne i saken, påkalla förhandling med hyresvärdsparten om fråga som rör enskild hyresgästs boendeförhållande eller tillämpning av enskilt hyresavtal.';
export const CLAUSE_7_LEAD = 'Begäran om förhandling ska göras skriftligen och innehålla följande:';
export const CLAUSE_7_ITEMS = [
    'uppgift om den eller de frågor som det begärs förhandling om',
    'uppgift om vilka fastigheter och lägenheter som berörs av förhandlingen',
    'förslag på tidpunkter och plats för sammanträde om den inte ska hållas digitalt.',
];
export const CLAUSE_7_TAIL = 'Framställning får göras digitalt enligt de instruktioner som följer av en särskild överenskommelse om digitaliserat förhandlingsförfarande.';
/**
 * Punkt 5 — Förhandlingsordningens innebörd (FO-4). Presentation only:
 * the four sub-frågor of a) render as a clean numbered list while the text
 * stays byte-identical to the verbatim clause body above.
 */
function Clause4() {
    return (_jsxs("div", { className: "my-3", style: { breakInside: 'avoid' }, children: [_jsx("div", { className: "mb-1 text-[11px] font-bold", children: "5. F\u00F6rhandlingsordningens inneb\u00F6rd" }), _jsx("p", { className: "m-0 text-[10.5px] leading-relaxed", children: CLAUSE_4_INTRO }), _jsx("p", { className: "mt-2 mb-0 text-[10.5px] leading-relaxed", children: CLAUSE_4A_LEAD }), _jsx("ol", { className: "mt-1 mb-0 list-decimal pl-6 text-[10.5px] leading-relaxed", children: CLAUSE_4A_ITEMS.map((item, i) => (_jsx("li", { className: "mt-0.5", children: item }, i))) }), _jsx("p", { className: "mt-2 mb-0 text-[10.5px] leading-relaxed", children: CLAUSE_4B }), _jsx("p", { className: "mt-2 mb-0 text-[10.5px] leading-relaxed", children: CLAUSE_4C })] }));
}
/**
 * Punkt 8 — Förhandlingsframställning (FO-5). Presentation only: the three
 * required contents of the framställning render with inline "(1) … (2) …
 * (3) …" numbering over the verbatim clause text.
 */
function Clause7() {
    return (_jsxs("div", { className: "my-3", style: { breakInside: 'avoid' }, children: [_jsx("div", { className: "mb-1 text-[11px] font-bold", children: "8. F\u00F6rhandlingsframst\u00E4llning" }), _jsxs("p", { className: "m-0 text-[10.5px] leading-relaxed", children: [CLAUSE_7_LEAD, ' ', CLAUSE_7_ITEMS.map((item, i) => (_jsxs("span", { children: ["(", i + 1, ") ", item, i < CLAUSE_7_ITEMS.length - 1 ? '; ' : ''] }, i)))] }), _jsx("p", { className: "mt-2 mb-0 text-[10.5px] leading-relaxed", children: CLAUSE_7_TAIL })] }));
}
export function Letterhead() {
    return (_jsx(TemplateLetterhead, { title: metadata.title, formNumber: metadata.formNumber }));
}
export function PageFooter() {
    return _jsx(TemplateFooter, { formNumber: metadata.formNumber });
}
export function Body({ values, signatories, }) {
    return (_jsxs(_Fragment, { children: [_jsx("p", { className: "mb-4 italic text-stone-600", children: metadata.preamble }), _jsxs("section", { style: { breakInside: 'avoid' }, children: [_jsx(Heading, { children: "1. Hyresv\u00E4rdspart" }), _jsx("div", { className: "grid grid-cols-2 gap-x-6", children: _jsx(PartyBlock, { metadata: metadata, slot: "lessor", values: values }) })] }), _jsxs("div", { className: "grid grid-cols-2 gap-6", children: [_jsxs("section", { style: { breakInside: 'avoid' }, children: [_jsx(Heading, { children: "2. Fastighets\u00E4garf\u00F6rening" }), _jsx(PartyBlock, { metadata: metadata, slot: "property_assoc", values: values })] }), _jsxs("section", { style: { breakInside: 'avoid' }, children: [_jsx(Heading, { children: "3. Hyresg\u00E4storganisation" }), _jsx(PartyBlock, { metadata: metadata, slot: "tenant_org", values: values })] })] }), _jsxs("section", { style: { breakInside: 'avoid' }, children: [_jsx(Heading, { children: "4. Omfattning och undantag" }), _jsxs("p", { className: "mt-1 mb-2 whitespace-pre-line text-[10.5px] leading-relaxed", children: ["F\u00F6rhandlingsordningen omfattar samtliga bostadsl\u00E4genheter i huset/husen inom f\u00F6ljande fastighet(-er):", '\n', _jsx("span", { className: "font-serif", children: v(values.property_designations) })] }), _jsxs("p", { className: "mb-2 whitespace-pre-line text-[10.5px] leading-relaxed", children: ["Samtliga adresser p\u00E5 fastigheten/fastigheterna:", '\n', _jsx("span", { className: "font-serif", children: v(values.property_addresses) })] }), _jsxs("p", { className: "mb-2 text-[10.5px] leading-relaxed", children: ["Antal l\u00E4genheter vid undertecknandet:", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: num(values.apartment_count) })] }), _jsx("p", { className: "text-[10.5px] leading-relaxed text-stone-700", children: "Hyresf\u00F6rhandlingslagens best\u00E4mmelser om n\u00E4r en l\u00E4genhet undandras fr\u00E5n en f\u00F6rhandlingsordning \u00E4r till\u00E4mpliga." })] }), _jsx(Clause4, {}), _jsx(ClauseBlock, { title: "6. F\u00F6rhandlingsskyldighet", body: 'Hyresvärdsparten och Hyresgästparten ("Parterna") har en förhandlingsskyldighet om förhandling har påkallats enligt punkten 5 a–c. Hyresgästsidan får endast lämna ut överenskommelser som ingåtts mellan Parterna till berörda hyresgäster eller när part är skyldig att lämna ut sådan överenskommelse till myndighet eller domstol.\n\n' +
                    'Parterna ska iaktta lojalitet och i övrigt genom ömsesidigt hänsynstagande till förhandlingsordningens långvarighet, söka att i samförstånd ordna sina gemensamma angelägenheter och såvitt möjligt förebygga uppkomsten av tvister.' }), _jsx(ClauseBlock, { title: "7. F\u00F6reningsr\u00E4tten", body: "Parterna ska l\u00E4mna f\u00F6reningsr\u00E4tten okr\u00E4nkt." }), _jsx(Clause7, {}), _jsx(ClauseBlock, { title: "9. Sammantr\u00E4de och protokoll", body: 'Parterna ska boka ett förhandlingssammanträde inom tre veckor efter att motparten fått del av förhandlingsframställningen om det inte rör sig om förhandling avseende en fråga där Parterna skriftligen kommit överens om annat. Förhandling ska bedrivas skyndsamt och lojalt. Respektive förhandlingspart ska framlägga motiverat förslag till lösning av de frågor som förhandlingen avser.\n\n' +
                    'Vid förhandling ska, om någon part begär det, föras protokoll som justeras av Parterna.' }), _jsx(ClauseBlock, { title: "10. F\u00F6rhandlings\u00F6verenskommelse", body: "F\u00F6rhandlings\u00F6verenskommelser ska uppr\u00E4ttas skriftligen och undertecknas av f\u00F6rhandlingsordningens parter. Parterna f\u00E5r underteckna f\u00F6rhandlings\u00F6verenskommelser genom elektronisk signering i enlighet med till\u00E4mplig lagstiftning, under f\u00F6ruts\u00E4ttning att signeringen m\u00F6jligg\u00F6r s\u00E4ker identifiering av undertecknaren och s\u00E4kerst\u00E4ller handlingens integritet." }), _jsx(ClauseBlock, { title: "11. Tvistl\u00F6sning genom s\u00E4rskild skiljeman", body: 'Om Parterna inte kommer överens, får respektive part hos relevant myndighet ansöka om utseendet av en särskild skiljeman för att denne ska pröva en tvist som omfattas av 23 a § hyresförhandlingslagen. Parterna ska omsätta den särskilda skiljemannens rekommendation i en förhandlingsöverenskommelse enligt hyresförhandlingslagen. Detta ska ske inom 14 dagar från det att rekommendationen lämnades.\n\n' +
                    'Parterna står för sina egna kostnader och ersättningen till skiljemannen ska fördelas lika mellan Parterna.' }), _jsx(ClauseBlock, { title: "12. Strandning", body: "Kan \u00F6verenskommelse inte tr\u00E4ffas ska f\u00F6rhandlingen avslutas genom att endera parten skriftligen meddelar att man fr\u00E5ntr\u00E4der f\u00F6rhandlingarna. F\u00F6r s\u00E5dan tvist som anges i 23 a \u00A7 hyresf\u00F6rhandlingslagen g\u00E4ller i st\u00E4llet vad som \u00E4r s\u00E4rskilt stadgat i 18 och 23 a \u00A7\u00A7 hyresf\u00F6rhandlingslagen." }), _jsx(ClauseBlock, { title: "13. Hyress\u00E4ttningsavgift", body: "Genom en f\u00F6rhandlings\u00F6verenskommelse f\u00E5r best\u00E4mmas att i hyran f\u00F6r samtliga l\u00E4genheter som omfattas av f\u00F6rhandlingsordningen ska ing\u00E5 ett visst belopp som utg\u00F6r ers\u00E4ttning till hyresg\u00E4storganisationen f\u00F6r dess f\u00F6rhandlingsarbete. Av f\u00F6rhandlings\u00F6verenskommelsen ska det framg\u00E5 hur stor del av totalhyran som utg\u00F6r ers\u00E4ttning av detta slag. Beloppet f\u00E5r inte \u00F6verstiga vad som kan anses sk\u00E4ligt med h\u00E4nsyn till hyran i \u00F6vrigt, f\u00F6rhandlingskostnadernas storlek och \u00F6vriga omst\u00E4ndigheter." }), _jsxs("section", { style: { breakInside: 'avoid' }, children: [_jsx(Heading, { children: "14. Avtalstid" }), values.agreement_term === TERM_TILLSVIDARE ? (_jsxs(_Fragment, { children: [_jsxs("p", { className: "mt-1 mb-2 text-[10.5px] leading-relaxed", children: ["Detta avtal g\u00E4ller fr\u00E5n och med", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.agreement_start_date) }), ' ', "och tills vidare."] }), _jsx("p", { className: "text-[10.5px] leading-relaxed text-stone-700", children: "Upps\u00E4gning av detta avtal ska ske skriftligen tidigast till \u00E5rsskifte som infaller n\u00E4rmast efter tre m\u00E5nader fr\u00E5n upps\u00E4gningen kommit motparten till handa. Upps\u00E4gningen ska delges motparten." })] })) : (_jsxs(_Fragment, { children: [_jsxs("p", { className: "mt-1 mb-2 text-[10.5px] leading-relaxed", children: ["Detta avtal g\u00E4ller fr\u00E5n och med", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.agreement_start_date) }), ' ', "till", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.agreement_end_date) }), "."] }), _jsx("p", { className: "text-[10.5px] leading-relaxed text-stone-700", children: "Har skriftlig upps\u00E4gning inte gjorts tre m\u00E5nader f\u00F6re avtalsperiodens utg\u00E5ng \u00E4r avtalet f\u00F6rl\u00E4ngt p\u00E5 ett \u00E5r i taget. Upps\u00E4gningen ska delges motparten." })] }))] }), _jsx(ClauseBlock, { title: "15. Ny f\u00F6rhandlingsordning", body: "Detta avtal om f\u00F6rhandlingsordning ers\u00E4tter, i f\u00F6rekommande fall, tidigare f\u00F6rhandlingsordning som g\u00E4llt f\u00F6r huset fram tills detta avtals ikrafttr\u00E4dande." }), _jsx(KeepTogether, { children: _jsx(SigningBlock, { statement: true, parties: [
                        { label: 'Hyresvärdspart', role: 'lessor', ordinal: 0 },
                        { label: 'Fastighetsägarförening', role: 'other', ordinal: 0 },
                        { label: 'Hyresgästorganisation', role: 'lessee', ordinal: 0 },
                    ], signatories: signatories }) })] }));
}
export function Component(props) {
    return (_jsx(TemplateDocument, { header: _jsx(Letterhead, {}), footer: _jsx(PageFooter, {}), children: _jsx(Body, { ...props }) }));
}
//# sourceMappingURL=index.js.map